import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { Loader2, AlertCircle, RefreshCw, Database, Search, X, Sparkles } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { useModels } from '@/hooks/useProjectDatabase'
import type { ModelInfo } from '@/types/database'

interface ModelListPanelProps {
  projectId: number
  selectedModel: { appLabel: string; modelName: string } | null
  onModelSelect: (appLabel: string, modelName: string) => void
  onInsertPrompt?: (prompt: string) => void
}

export function ModelListPanel({ projectId, selectedModel, onModelSelect, onInsertPrompt }: ModelListPanelProps) {
  const { t } = useTranslation('conversations')
  const { data, isLoading, error, reload, isReloading } = useModels(projectId)
  const [searchQuery, setSearchQuery] = useState('')

  // Group models by app_label
  const groupedModels = useMemo(() => {
    if (!data?.models) return {}

    const filtered = searchQuery
      ? data.models.filter(m =>
          m.model_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          m.verbose_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          m.app_label.toLowerCase().includes(searchQuery.toLowerCase())
        )
      : data.models

    return filtered.reduce<Record<string, ModelInfo[]>>((acc, model) => {
      const label = model.app_label.toUpperCase()
      if (!acc[label]) acc[label] = []
      acc[label].push(model)
      return acc
    }, {})
  }, [data?.models, searchQuery])

  const appLabels = Object.keys(groupedModels).sort()

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-4 text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin mb-2" />
        <span className="text-sm">{t('database.loading', 'Loading...')}</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-4 text-center">
        <AlertCircle className="h-6 w-6 text-destructive mb-2" />
        <span className="text-sm text-destructive mb-3">{t('database.loadError', 'Failed to load models')}</span>
        <Button variant="outline" size="sm" onClick={() => reload()} disabled={isReloading}>
          <RefreshCw className={cn('h-3.5 w-3.5 mr-1.5', isReloading && 'animate-spin')} />
          {t('database.retry', 'Retry')}
        </Button>
      </div>
    )
  }

  if (!data?.models?.length) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-4 text-center text-muted-foreground">
        <Database className="h-8 w-8 mb-2 opacity-50" />
        <span className="text-sm mb-3">{t('database.noModels', 'No user-defined models found')}</span>
        <Button variant="outline" size="sm" onClick={() => reload()} disabled={isReloading}>
          <RefreshCw className={cn('h-3.5 w-3.5 mr-1.5', isReloading && 'animate-spin')} />
          {t('database.refreshModels', 'Refresh models')}
        </Button>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Header with refresh */}
      <div className="flex items-center justify-between px-3 h-10 border-b bg-muted/30 flex-shrink-0">
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          {t('database.models', 'Models')}
        </span>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6"
          onClick={() => reload()}
          disabled={isReloading}
          title={t('database.refreshModels', 'Refresh models')}
        >
          <RefreshCw className={cn('h-3.5 w-3.5', isReloading && 'animate-spin')} />
        </Button>
      </div>

      {/* Search input */}
      <div className="px-2 py-2 border-b flex-shrink-0">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder={t('database.searchModels', 'Search models...')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-7 text-xs pl-7 pr-7"
          />
          {searchQuery && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-0 top-1/2 -translate-y-1/2 h-7 w-7"
              onClick={() => setSearchQuery('')}
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>

      {/* Model list */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {appLabels.length === 0 ? (
          <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
            {t('database.noMatches', 'No matching models')}
          </div>
        ) : (
          <div className="py-1">
            {appLabels.map((appLabel) => (
              <div key={appLabel} className="mb-2">
                {/* App label header */}
                <div className="px-3 py-1.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                  {appLabel}
                </div>
                {/* Models in this app */}
                {groupedModels[appLabel].map((model) => {
                  const isSelected =
                    selectedModel?.appLabel === model.app_label &&
                    selectedModel?.modelName === model.model_name
                  return (
                    <button
                      key={`${model.app_label}-${model.model_name}`}
                      onClick={() => onModelSelect(model.app_label, model.model_name)}
                      className={cn(
                        'w-full px-3 py-1.5 flex items-center justify-between text-left',
                        'hover:bg-muted/50 transition-colors',
                        isSelected && 'bg-muted'
                      )}
                    >
                      <span className="text-sm truncate">
                        {model.verbose_name || model.model_name}
                      </span>
                      <Badge variant="secondary" className="ml-2 text-[10px] px-1.5 py-0 h-5 flex-shrink-0">
                        {model.record_count}
                      </Badge>
                    </button>
                  )
                })}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Visualize button - fixed at bottom */}
      {onInsertPrompt && (
        <div className="border-t px-2 py-2 flex-shrink-0">
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={() => onInsertPrompt(t('database.visualizePrompt', 'Draw an Excalidraw diagram of the data schema'))}
          >
            <Sparkles className="h-3.5 w-3.5 mr-1.5" />
            {t('database.visualizeButton', 'Visualize')}
          </Button>
        </div>
      )}
    </div>
  )
}
